import React from 'react'
import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  MultiSelect,
  Popover,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import { useDisclosure } from '@mantine/hooks'
import { QuestionMarkCircledIcon, TrashIcon } from '@radix-ui/react-icons'

import { BooleanSelect } from '@/components/common/BooleanSelect'
import { RemoveAttendeeButton } from '@/components/Event/attendee/AttendeeControls'
import { useAttendeeField } from '@/components/Event/attendee/useAttendeeField'
import { canRemoveAttendeeFromWitnessSection } from '@/components/Event/CrForm/witnesses'
import { transformToZoomParticipants } from '@/components/Event/NewScript/utils/attendees'
import { StateSelect } from '@/components/StateSelect'
import { AttendeeRole } from '@/constants'
import { AttendeeFormValues, ReporterFeedback, ZoomParticipant } from '@/types'
import { STATES_CODES } from '@/utils/states'
import { formatTimeInTimezone } from '@/utils/time'

export const CrAttendeeInput: React.FC<{
  roles: AttendeeRole[]
  onBlur?: () => void
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
  index: number
  attendeeWarnings: Set<keyof AttendeeFormValues>[]
  // Witness City/State only shows where witness location is required (AZ), per DR Script.
  witnessLocationEnabled?: boolean
  // Real Zoom participants for the "Zoom account name before renaming" mapping.
  zoomParticipants?: ZoomParticipant[]
  timezone?: string
}> = ({
  roles,
  onBlur,
  form,
  index,
  attendeeWarnings,
  witnessIndex,
  witnessLocationEnabled = false,
  zoomParticipants = [],
  timezone,
}) => {
  const {
    values,
    valuePrefix,
    updateValueAndPropagateForward,
    roleData,
    formWarnings,
  } = useAttendeeField({ form, witnessIndex, index, roles, attendeeWarnings })

  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false)
  const canRemoveAttendee = canRemoveAttendeeFromWitnessSection({
    attendeeRole: values?.role,
  })

  return (
    <Flex
      align="center"
      columnGap="sm"
      data-attendee-id={values?.id ?? undefined}
    >
      <Stack spacing="sm" sx={{ flex: 1 }}>
        <Group grow noWrap align="flex-start">
          <TextInput
            required
            label="Name"
            {...form.getInputProps(`${valuePrefix}.name`)}
            onChange={event =>
              updateValueAndPropagateForward('name', event.target.value)
            }
            {...formWarnings.getInputWarningProps(index, 'name')}
          />
          <TextInput
            label="Email"
            type="email"
            {...form.getInputProps(`${valuePrefix}.email`)}
            value={values?.email ?? ''}
            onChange={event =>
              updateValueAndPropagateForward(
                'email',
                event.target.value || null,
              )
            }
            {...formWarnings.getInputWarningProps(index, 'email')}
          />
        </Group>
        <Group grow noWrap align="flex-start">
          <Select
            required
            styles={{ item: { wordBreak: 'normal' } }}
            label="Role"
            data={roleData}
            {...form.getInputProps(`${valuePrefix}.role`)}
            // Role is per-witness (each segment has its own Witness), so unlike the other
            // fields it is NOT propagated forward — mirroring the DR script.
            onChange={value => form.setFieldValue(`${valuePrefix}.role`, value)}
            {...formWarnings.getInputWarningProps(index, 'role')}
          />
          <BooleanSelect
            required
            label="Joined Zoom"
            value={values?.present ?? null}
            // Same person is present across all witness segments — propagate forward so
            // every witness's coverage/presence validation sees it, not just the active one.
            onChange={value => updateValueAndPropagateForward('present', value)}
            error={Boolean(form.errors[`${valuePrefix}.present`])}
          />
        </Group>
        {values?.present === true && zoomParticipants.length > 0 && (
          <>
            <MultiSelect
              searchable
              label={
                <Group spacing={4}>
                  Zoom account name before renaming{' '}
                  <Text size="sm" component="span" color="red">
                    *
                  </Text>
                  <Tooltip
                    label="Select the original Zoom display name for this attendee. If they rejoined the call, select each entry. If two attendees joined from the same location, select the same Zoom account for both."
                    multiline
                    width={400}
                  >
                    <QuestionMarkCircledIcon />
                  </Tooltip>
                </Group>
              }
              description="Map this attendee to the Zoom participant(s) they joined as."
              data={zoomParticipants.map(zp => ({
                value: zp.id,
                label: timezone
                  ? `${zp.user_name} (${formatTimeInTimezone(
                      zp.join_time,
                      timezone,
                    )})`
                  : zp.user_name,
              }))}
              value={(values?.zoom_participants ?? []).map(zp => zp.id)}
              error={form.errors[`${valuePrefix}.zoom_participants`]}
              onChange={ids => {
                // Propagate the mapping to this attendee across all following witnesses so
                // each witness's zoom_coverage passes — the meeting/participants are shared.
                updateValueAndPropagateForward(
                  'zoom_participants',
                  transformToZoomParticipants(ids, zoomParticipants),
                )
                if (ids.length) {
                  form.clearFieldError(`${valuePrefix}.zoom_participants`)
                }
              }}
            />
            <Alert color="indigo" variant="light">
              Confirm that you have renamed this attendee in Zoom to their full
              name.
            </Alert>
          </>
        )}
        {values?.role === AttendeeRole.WITNESS && witnessLocationEnabled && (
          <Group grow noWrap align="flex-start">
            <TextInput
              required
              label="City"
              value={values?.city ?? ''}
              error={form.errors[`${valuePrefix}.city`]}
              onChange={event =>
                form.setFieldValue(`${valuePrefix}.city`, event.target.value)
              }
            />
            <StateSelect
              required
              label="State"
              placeholder="Pick one"
              states={STATES_CODES}
              value={values?.state ?? null}
              error={form.errors[`${valuePrefix}.state`]}
              onChange={value =>
                form.setFieldValue(`${valuePrefix}.state`, value)
              }
            />
          </Group>
        )}
      </Stack>

      <Box w={28} sx={{ flex: 'none' }}>
        {canRemoveAttendee && (
          <Popover
            opened={confirmOpened}
            onChange={opened => (opened ? openConfirm() : closeConfirm())}
            position="left"
            withArrow
            shadow="md"
            width={240}
          >
            <Popover.Target>
              <RemoveAttendeeButton onClick={openConfirm} onBlur={onBlur}>
                <TrashIcon width={20} height={20} />
              </RemoveAttendeeButton>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack spacing="sm">
                <Text size="sm">
                  Remove attendee{values?.name ? ` ${values.name}` : ''}?
                </Text>
                <Group spacing="sm" position="right">
                  <Button variant="default" size="xs" onClick={closeConfirm}>
                    Cancel
                  </Button>
                  <Button
                    color="red"
                    size="xs"
                    onClick={() => {
                      form.removeListItem(
                        `events_feedbacks.${witnessIndex}.attendees`,
                        index,
                      )
                      closeConfirm()
                    }}
                  >
                    Remove
                  </Button>
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )}
      </Box>
    </Flex>
  )
}
